import React, { useRef, useState, useCallback, useEffect } from 'react'

import {
    Box,
    Button,
    Tooltip
} from '@mui/material';

import { useNavigate, Link } from 'react-router-dom';

// import DateFilter from '@inovua/reactdatagrid-community/DateFilter'
import { icons } from '../../common/CommonIcons';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonRestricted from '../../common/CommonRestricted';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
// import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';

import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useAppContextFeatures } from '../../../context/AppContext';
import { useDocuments, documentTypes, targetTypes } from '../../../hooks/api/useDocuments';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

import { useAuthAccess } from '../../../hooks/useAuthAccess';

import { adjustmentTypes } from '../../../schema/documents/itemAdjustmentSchema';

export default function ListItemAdjustments({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list, remove } = useDocuments({ documentType: documentTypes.itemAdjustment });

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnTextDisplay } = useDataGridParams('id')
    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)
    const { getLocationIds } = useAppContextFeatures();

    const gridRef = React.createRef();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.itemAdjustment.id)

    const { getAvailableLocations } = useAuthAccess()

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    useEffect(() => {

        setRefreshDataGrid(val => val + 1)

    }, [getLocationIds])

    const fetchData = async (props) => {

        // console.log('getAvailableLocations', getAvailableLocations())

        resetDataGridTableSelection();

        try {

            props.customFilterLocationEnable = true;
            props.customFilterLocationFields = ['location_id']

            const result = await list(props)

            let data = [];

            // need to check the data is allow verify in or out
            // const allowLocationIds = getAvailableLocations().map(i => i.id)

            result?.data?.documents.forEach(e1 => {

                let verifyInItems = [];
                let verifyOutItems = [];

                e1.items.forEach((e2) => {
                    if (e2.adjustment_type === adjustmentTypes.increase.id) {
                        verifyInItems.push(e2)
                    } else {
                        verifyOutItems.push(e2)
                    }
                })

                // console.log('allowLocationIds', allowLocationIds)

                // e1.allowVerifyIn = false;
                // e1.allowVerifyOut = false;

                // // check if verify in allow
                // if(allowLocationIds.includes(e1.location_id)){
                //     e1.allowVerifyOut = true
                // }

                // // check if verify out allow
                // if(allowLocationIds.includes(e1.to_location_id)){
                //     e1.allowVerifyIn = true
                // }

                data.push({
                    ...e1,
                    verifyInItems,
                    verifyOutItems
                })

            })

            return {
                data,
                count: result?.data?.total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }

    }

    useEffect(() => {

        defineColumnsView()

    }, [])

    const defineColumnsView = () => {

        defineColumns([
            {
                name: 'location_name', header: t('location')
            },
            {
                name: 'doc_number',
                header: t('docNo'),
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageView.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`documents/itemAdjustment/view/${cellProps.data.id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'doc_date', header: t('date')
            },
            {
                name: 'verifyInItems',
                header: t('validateIn'),
                renderValue: ({ value, cellProps }) => {

                    const doneValidation = value.reduce((total, item) => total + parseFloat(item?.verify_in_qty), 0)
                    const totalValidation = value.reduce((total, item) => total + parseFloat(item?.qty), 0)

                    if (parseFloat(totalValidation) === 0) {
                        return <></>
                    }

                    return (
                        <Box
                            display={"flex"}
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                        >
                            <Tooltip
                                title={
                                    doneValidation === totalValidation ? t("validated") : t("pending")
                                }
                                placement="right"
                            >
                                <span>
                                    <Button
                                        disabled={
                                            !value
                                        }
                                        size='small'
                                        variant={doneValidation === totalValidation ? "contained" : "outlined"}
                                        color={`primary`}
                                        sx={{
                                            textAlign: "center",
                                            width: 100
                                        }}
                                        onClick={() => {

                                            if (isPermissionAllow({ functionType: functionTypes.pageVerifyIn.id })) {
                                                navigate(getRoute(`documents/itemAdjustment/verifyIn/${cellProps.data?.id}`))
                                            }

                                        }}
                                    >
                                        {doneValidation} / {totalValidation}

                                    </Button>
                                </span>

                            </Tooltip>

                        </Box>
                    )

                }
            },
            {
                name: 'verifyOutItems',
                header: t('validateOut'),
                renderValue: ({ value, cellProps }) => {

                    const doneValidation = value.reduce((total, item) => total + parseFloat(item?.verify_out_qty), 0)
                    const totalValidation = value.reduce((total, item) => total + parseFloat(item?.qty), 0)

                    if (parseFloat(totalValidation) === 0) {
                        return <></>
                    }

                    return (
                        <Box
                            display={"flex"}
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                        >
                            <Tooltip
                                title={
                                    doneValidation === totalValidation ? t("validated") : t("pending")
                                }
                                placement="right"
                            >
                                <span>
                                    <Button
                                        disabled={
                                            !value
                                        }
                                        size='small'
                                        variant={doneValidation === totalValidation ? "contained" : "outlined"}
                                        color={`primary`}
                                        sx={{
                                            textAlign: "center",
                                            width: 100
                                        }}
                                        onClick={() => {

                                            if (isPermissionAllow({ functionType: functionTypes.pageVerifyOut.id })) {
                                                navigate(getRoute(`documents/itemAdjustment/verifyOut/${cellProps.data?.id}`))
                                            }

                                        }}
                                    >
                                        {doneValidation} / {totalValidation}

                                    </Button>
                                </span>

                            </Tooltip>

                        </Box>
                    )

                }
            },
        ])

    }

    const handleDelete = async () => {

        try {

            let result = await remove({ ids: selectedIds, targetType: targetTypes.itemAdjustment })

            promptMessage(true, result?.message)
            setRefreshDataGrid(val => val + 1)

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {

                        navigate(getRoute("documents/itemAdjustment/new"));

                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn:
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        // console.log('items selected : ', items)
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />
        )

    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.itemAdjustments })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("itemAdjustments")}
                breadcrumbs={[
                    {
                        displayText: t("itemAdjustments"),
                        props: {
                            // underline: "hover",
                            // href: "/"
                        }
                    }
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}
